import { useState } from 'react';
import { KeyRound, LogOut, RefreshCw, RotateCcw, Trash2 } from 'lucide-react';
import { api, send } from '../../api';
import { Button } from '../ui/button';
import { ConfirmDialog, Sheet } from '../ui/side-sheet';
import type { MenuItem } from '../ui/menu';
import quickbooksLogo from '../../assets/logos/quickbooks.svg';
import { EnvironmentBadge, IntegrationCard, Logo, type CardMessage, type Fact } from './IntegrationCard';
import { booksFreshness, quickbooksHealth, relativeTime, sandboxLine, sourceRunLine, withFreshness, type IntegrationsState } from './model';

const message = (e: unknown) => e instanceof Error ? e.message : 'This step could not be completed.';
const reassurance = 'Read-only: Alder never changes anything in QuickBooks.';

/**
 * QuickBooks Online. Two steps: save the Intuit app (client id, secret, environment), then Connect QuickBooks, which
 * opens Intuit's consent screen; Intuit returns here with ?quickbooks=connected or failed. The card shows the exact
 * redirect URI to register in the Intuit developer portal.
 */
export function QuickBooksCard({ state, onChanged, onSyncNow, syncing }: {
  state: IntegrationsState; onChanged: () => Promise<void>; onSyncNow: () => void; syncing: boolean;
}) {
  const status = state.quickbooks!;
  const outcome = new URLSearchParams(window.location.search).get('quickbooks');
  const [open, setOpen] = useState(false); const [confirming, setConfirming] = useState<'connection' | 'app' | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [environment, setEnvironment] = useState(status.environment ?? 'sandbox');
  const [busy, setBusy] = useState(false); const [formError, setFormError] = useState('');
  const [result, setResult] = useState<CardMessage>(null);
  const connection = status.connection && status.connection !== 'error' ? status.connection : null;
  const hasApp = status.source === 'saved' || status.source === 'env';
  const freshness = connection ? booksFreshness(state) : null;
  const health = withFreshness(quickbooksHealth(state, outcome), freshness);
  const environmentNow = connection?.environment ?? status.environment;
  const badge = connection && environmentNow === 'sandbox'
    ? { label: 'Sandbox copy', text: sandboxLine('QuickBooks', Boolean(state.shadowMode)) } : null;
  const summary = connection || hasApp ? 'Alder reads invoices, customers and payments from QuickBooks.'
    : 'Connect QuickBooks Online so Alder reads invoices, customers and payments. About 5 minutes: save the Intuit app, then approve access.';

  function openSheet() { setValues({}); setEnvironment(status.environment ?? 'sandbox'); setFormError(''); setOpen(true); }
  async function save() {
    setBusy(true); setFormError('');
    try {
      await send('/app-api/integrations/quickbooks', 'PUT', { clientId: values.clientId ?? '', clientSecret: values.clientSecret ?? '',
        environment });
      setOpen(false); setValues({});
      setResult({ kind: 'success', text: 'QuickBooks app saved. Choose Connect QuickBooks to pick the company.' });
      await onChanged();
    } catch (e) { setFormError(message(e)); } finally { setBusy(false); }
  }
  async function connect() {
    setBusy(true); setResult(null);
    try { const { url } = await api<{ url: string }>('/app-api/integrations/quickbooks/connect', {}); window.location.assign(url); }
    catch (e) { setResult({ kind: 'error', text: message(e) }); setBusy(false); }
  }
  async function remove(what: 'connection' | 'app') {
    setBusy(true);
    try {
      await send(what === 'connection' ? '/app-api/integrations/quickbooks/connection' : '/app-api/integrations/quickbooks', 'DELETE');
      setResult({ kind: 'success', text: what === 'connection' ? 'QuickBooks disconnected. Its tokens were deleted.'
        : 'QuickBooks removed. The saved app and connection were deleted.' });
      await onChanged();
    } catch (e) { setResult({ kind: 'error', text: message(e) }); } finally { setConfirming(null); setBusy(false); }
  }

  const facts: Fact[] = [];
  if (hasApp || status.source === 'error') {
    facts.push({ label: 'Environment', value: <EnvironmentBadge environment={connection?.environment ?? status.environment}/> });
    facts.push({ label: 'Client id', value: <code className="masked">{status.fields.clientId ?? 'Not set'}</code> });
  }
  if (connection) {
    facts.push({ label: 'Company (realm)', value: <code>{connection.realmId}</code> });
    facts.push({ label: 'Connected', value: relativeTime(connection.connectedAt) });
    const run = sourceRunLine('quickbooks', state.last);
    if (run) facts.push({ label: 'Last sync', value: run });
  }
  facts.push({ label: 'Redirect URI to register at Intuit', value: <code>{status.redirectUri}</code>, wide: true, copy: status.redirectUri });
  const keyFacts: Fact[] = connection ? [{ label: 'Company', value: <code>{connection.realmId}</code> }] : [];

  const canEdit = state.canSave;
  const primary = !state.live ? null : !hasApp
    ? <Button size="sm" disabled={!canEdit} onClick={openSheet}>Set up QuickBooks</Button>
    : !connection ? <Button size="sm" disabled={busy || !status.canConnect} onClick={() => void connect()}>
      {busy ? 'Opening Intuit…' : 'Connect QuickBooks'}</Button> : null;
  const menu: MenuItem[] = [];
  if (connection) {
    menu.push({ label: 'Sync now', icon: <RefreshCw size={15} aria-hidden/>, onSelect: onSyncNow, disabled: syncing || !state.live });
    menu.push({ label: 'Reconnect QuickBooks', icon: <RotateCcw size={15} aria-hidden/>, disabled: busy || !status.canConnect,
      onSelect: () => void connect() });
  }
  if (hasApp && canEdit && !connection) menu.push({ label: 'Replace app credentials', icon: <KeyRound size={15} aria-hidden/>,
    onSelect: openSheet });
  if (connection) menu.push({ label: 'Disconnect', icon: <LogOut size={15} aria-hidden/>, danger: true,
    onSelect: () => setConfirming('connection') });
  if (status.source === 'saved') menu.push({ label: 'Remove QuickBooks', icon: <Trash2 size={15} aria-hidden/>, danger: true,
    onSelect: () => setConfirming('app') });
  const shown: CardMessage = result ?? (outcome === 'connected' && connection
    ? { kind: 'success', text: `QuickBooks is connected to company ${connection.realmId}. Choose Sync now to read it.` } : null);

  return <>
    <IntegrationCard id="quickbooks" logo={quickbooksLogo} logoFill name="QuickBooks" role="Accounting · read-only" health={health}
      summary={summary} freshness={freshness} badge={badge} keyFacts={keyFacts} facts={facts} reassurance={reassurance}
      message={shown} primary={primary} menu={menu}/>
    <Sheet open={open} onClose={() => setOpen(false)} title="QuickBooks app"
      description="The Intuit developer app Alder signs in with. You pick the company on Intuit's screen next."
      icon={<Logo src={quickbooksLogo} fill/>}
      footer={<>
        <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
        <Button disabled={busy || !canEdit} onClick={() => void save()}>{busy ? 'Saving…' : 'Save'}</Button>
      </>}>
      <form className="sheet-form" onSubmit={event => { event.preventDefault(); void save(); }}>
        {status.source === 'env' && <p className="sheet-note">Running on host settings now. Once you save here, these values
          replace the host settings for QuickBooks.</p>}
        <p className="sheet-note">In the Intuit developer portal, add this redirect URI to the app&apos;s Keys &amp; OAuth
          settings exactly as shown: <code>{status.redirectUri}</code></p>
        {(['clientId', 'clientSecret'] as const).map(key => <label key={key}>
          <span>{key === 'clientId' ? 'Client id' : 'Client secret'}</span>
          <input type={key === 'clientSecret' ? 'password' : 'text'} autoComplete="off" spellCheck={false} value={values[key] ?? ''}
            onChange={event => setValues({ ...values, [key]: event.target.value })} placeholder={status.fields[key] ?? ''}/>
        </label>)}
        <fieldset className="segmented"><legend>Environment</legend>
          {(['sandbox', 'production'] as const).map(value => <label key={value}>
            <input type="radio" name="quickbooks-environment" value={value} checked={environment === value}
              onChange={() => setEnvironment(value)}/><span>{value === 'sandbox' ? 'Sandbox' : 'Production'}</span></label>)}
        </fieldset>
        {status.source === 'saved' && <p className="sheet-hint">Leave a field blank to keep its saved value.</p>}
        <p className="sheet-assurance">{reassurance} Values are saved encrypted; saved keys show only their last four
          characters. Connect QuickBooks is the test: Intuit checks the app when you approve access.</p>
        {formError && <p className="integration-error" role="alert">{formError}</p>}
        <button type="submit" hidden/>
      </form>
    </Sheet>
    <ConfirmDialog open={confirming !== null} busy={busy}
      title={confirming === 'app' ? 'Remove QuickBooks?' : 'Disconnect QuickBooks?'}
      confirmLabel={confirming === 'app' ? 'Remove QuickBooks' : 'Disconnect QuickBooks'}
      body={confirming === 'app' ? 'Alder deletes the saved Intuit app and the connection. Invoices already synced stay in Alder.'
        : 'Alder stops reading QuickBooks and deletes its tokens. Invoices already synced stay in Alder.'}
      onConfirm={() => void remove(confirming ?? 'connection')} onClose={() => setConfirming(null)}/>
  </>;
}
